// @ts-check
// Conversa do atendimento na Caixa (contrato 10 de atendimento, seções 8 a 16 e 24.2): cabeçalho com protocolo, assunto
// (clique edita), situação, prioridade, departamento, responsável, etiquetas e prazo; ações Assumir, Transferir,
// Consultar, Resolver e o menu (mudar a finalidade, dividir, mesclar, Ouvidoria, Proteção, abrir o registro, responder
// pelo WhatsApp); a linha única com mensagens, notas e eventos; a faixa do bot; o campo de resposta com a colisão.
// Atualiza a cada 10 s com a aba visível e informa a presença (vendo ou digitando) no mesmo ritmo.
import { html } from 'htm/preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { getThread, listMessages } from '../../data/conversations.js';
import {
  addServiceNote, applyReplyTemplateActions, deleteServiceNote, editServiceNote, getTicket, getWhoIs, reopenTicket, revealManifestationIdentity, reportPresence, sendServiceMessage, takeTicket, updateTicket,
} from '../../data/service-desk.js';
import { ApiError } from '../../data/api-client.js';
import { currentWorkspaceMemberState } from '../../data/metadata-store.js';
import { workspaceMembersState, loadWorkspaceMembers } from '../../data/workspace-members.js';
import { formatFullName } from '../../lib/field-metadata.js';
import { buildReplySubject } from '../../lib/conversation-format.js';
import { describeCollision, isTicketOpen, latestIncomingMessageId } from '../../lib/service-inbox-rules.js';
import { canChangeTicketStatus } from '../../../../shared/service-desk/ticket-statuses.js';
import { SERVICE_TAGS, TICKET_PRIORITIES } from '../../../../shared/service-desk/ticket-values.js';
import { navigate } from '../../app/router.js';
import { openRecord } from '../side-panel/open-record.js';
import { openNewConversationPanel } from '../side-panel/side-panel-state.js';
import { usePolling } from '../conversations/conversations-state.js';
import { TemplateMessageModal } from '../conversations/template-message-modal.js';
import { BotConversationBanner } from '../bots/bot-conversation-banner.js';
import { getThreadDisplayName } from '../conversations/conversation-display.js';
import { Button, LightIconButton } from '../../ui/button.js';
import { Dropdown, DropdownMenuItems, DropdownMenuSeparator, MenuItem } from '../../ui/dropdown.js';
import { EmptyState, Loader } from '../../ui/feedback.js';
import { Icon } from '../../ui/icon.js';
import { showSuccessSnackbar } from '../../ui/snackbar.js';
import { ServiceTimeline } from './service-timeline.js';
import { ServiceComposer } from './service-composer.js';
import { ConsultationsPanel, CsatPanel, ManifestationPanel, ProtectionPanel, RetentionPanel } from './service-ticket-panels.js';
import {
  ConsultDialog, MergeDialog, OmbudsmanEscalateDialog, PurposeDialog, ProtectionDialog, ResolveDialog, SplitDialog, TransferDialog,
} from './service-dialogs.js';
import { RestrictedBadge, ServiceChannelIcon, SlaChip, TicketValueTag, showServiceError, ticketValueLabel } from './service-display.js';
import { t } from '../../i18n/index.js';

const POLLING_INTERVAL_MILLISECONDS = 10_000;
// a presença de quem digita vale por 15 s depois da última tecla
const TYPING_WINDOW_MILLISECONDS = 15_000;

/**
 * @typedef {import('../../data/service-desk.js').TicketDetail} TicketDetail
 * @typedef {import('../../data/service-desk.js').ServiceTeam} ServiceTeam
 * @typedef {import('../../data/service-desk.js').ServiceMe} ServiceMe
 * @typedef {'transfer' | 'consult' | 'resolve' | 'split' | 'merge' | 'protection' | 'ombudsman' | 'purpose' | 'templateMessage'} PaneDialog
 */

/**
 * Menu simples ancorado num botão (situação, prioridade, responsável, etiquetas e o "Mais").
 * @param {{ label: any, icon?: string, testId?: string, children: (close: () => void) => any, className?: string, ariaLabel?: string, disabled?: boolean }} props
 */
function MenuButton({ label, icon, testId, children, className = 'service-chip-button', ariaLabel, disabled = false }) {
  const anchorRef = useRef(/** @type {HTMLButtonElement | null} */ (null));
  const [isOpen, setIsOpen] = useState(false);
  return html`<span class="service-menu-anchor">
    <button type="button" ref=${anchorRef} class=${className} data-testid=${testId} aria-haspopup="menu" aria-expanded=${isOpen ? 'true' : 'false'}
      aria-label=${ariaLabel} disabled=${disabled} onClick=${() => setIsOpen(!isOpen)}>
      ${icon ? html`<${Icon} name=${icon} size=${14} />` : null}${label}
    </button>
    <${Dropdown} anchorRef=${anchorRef} isOpen=${isOpen} onClose=${() => setIsOpen(false)} width=${240} role="menu">
      <${DropdownMenuItems} hasMaxHeight=${true}>${children(() => setIsOpen(false))}<//>
    <//>
  </span>`;
}

/**
 * @param {{ ticketId: string | null, threadId: string | null, me: ServiceMe | null, teams: ServiceTeam[], isWhoIsOpen: boolean, onToggleWhoIs: () => void,
 *   onBack?: (() => void) | null, onChanged: () => void, commandRequest: { action: string, nonce: number } | null, onTicketLoaded?: (detail: TicketDetail | null) => void,
 *   onWhoIsReload?: () => void, notifyFamilyDefault?: boolean, showWhoIsToggle?: boolean }} props
 *   showWhoIsToggle: a Ouvidoria abre o atendimento sem o "Quem é" (a identidade segue o modo da manifestação)
 */
export function ServiceTicketPane({
  ticketId, threadId, me, teams, isWhoIsOpen, onToggleWhoIs, onBack = null, onChanged, commandRequest, onTicketLoaded, onWhoIsReload, notifyFamilyDefault = true, showWhoIsToggle = true,
}) {
  const [detail, setDetail] = useState(/** @type {TicketDetail | null} */ (null));
  const [thread, setThread] = useState(/** @type {any} */ (null));
  const [messages, setMessages] = useState(/** @type {any[]} */ ([]));
  const [status, setStatus] = useState(/** @type {'loading' | 'ready' | 'notFound'} */ ('loading'));
  const [mode, setMode] = useState(/** @type {'reply' | 'note'} */ ('reply'));
  const [focusRequest, setFocusRequest] = useState(0);
  const [dialog, setDialog] = useState(/** @type {PaneDialog | null} */ (null));
  const [splitSelection, setSplitSelection] = useState(/** @type {Set<string> | null} */ (null));
  const [presence, setPresence] = useState(/** @type {{ others: any[], latestIncomingMessageId: string | null }} */ ({ others: [], latestIncomingMessageId: null }));
  // a última mensagem que a pessoa viu: a mensagem nova da família depois dela bloqueia o envio até ser vista
  const [acknowledged, setAcknowledged] = useState(/** @type {{ incomingId: string | null, messageId: string | null }} */ ({ incomingId: null, messageId: null }));
  const [isEditingSubject, setIsEditingSubject] = useState(false);
  const [subjectDraft, setSubjectDraft] = useState('');
  // identidade da manifestação protegida, pedida por "Ver identidade" (cada pedido fica na auditoria)
  const [revealedIdentity, setRevealedIdentity] = useState(/** @type {{ name: string | null, email: string | null, phone: string | null } | null} */ (null));
  // atendimentos da mesma família (do "Quem é"): candidatos a mesclar e o pedido de transferência que a retenção não segura
  const [familyTickets, setFamilyTickets] = useState(/** @type {any[]} */ ([]));
  const lastTypingRef = useRef(0);
  const viewerId = currentWorkspaceMemberState.value?.id ?? null;
  const ticket = detail?.ticket ?? null;
  const myTeamIds = new Set((me?.teams ?? []).map((candidate) => candidate.id));
  const isOpen = ticket ? isTicketOpen(ticket) : true;
  const canTake = Boolean(ticket) && isOpen && ticket?.assignee?.workspaceMemberId !== viewerId && myTeamIds.has(ticket?.team?.id ?? '');
  const canReply = Boolean(thread) && (!ticket || isOpen || ticket.status === 'RESOLVED');

  /** @param {{ isInitial?: boolean }} [options] */
  const load = async ({ isInitial = false } = {}) => {
    try {
      /** @type {TicketDetail | null} */
      let nextDetail = null;
      let nextThread = null;
      if (ticketId) {
        nextDetail = await getTicket(ticketId);
        nextThread = nextDetail?.threads?.[0] ?? null;
      } else if (threadId) {
        nextThread = await getThread(threadId);
      }
      if (!nextDetail && !nextThread) {
        setStatus('notFound');
        return;
      }
      const page = nextThread ? await listMessages(nextThread.id, { limit: 50 }) : { messages: [] };
      setDetail(nextDetail);
      setThread(nextThread);
      setMessages(page.messages);
      onTicketLoaded?.(nextDetail);
      if (isInitial) {
        setAcknowledged({ incomingId: latestIncomingMessageId(page.messages), messageId: page.messages.at(-1)?.id ?? null });
      }
      setStatus('ready');
    } catch (error) {
      if (error instanceof ApiError && error.statusCode === 404) setStatus('notFound');
      else if (isInitial) {
        showServiceError(error);
        setStatus('notFound');
      }
    }
  };

  useEffect(() => {
    setDetail(null);
    setThread(null);
    setMessages([]);
    setDialog(null);
    setSplitSelection(null);
    setIsEditingSubject(false);
    setRevealedIdentity(null);
    setPresence({ others: [], latestIncomingMessageId: null });
    setStatus('loading');
    load({ isInitial: true });
  }, [ticketId, threadId]);
  useEffect(() => {
    loadWorkspaceMembers();
  }, []);
  useEffect(() => {
    if (!ticket || (dialog !== 'merge' && ticket.ticketType !== 'RETENTION')) return;
    getWhoIs({ ticketId: ticket.id }).then((whoIs) => setFamilyTickets(whoIs?.previousTickets ?? [])).catch(() => setFamilyTickets([]));
  }, [ticket?.id, dialog === 'merge']);
  const revealIdentity = async () => {
    if (!ticketId) return;
    try {
      setRevealedIdentity(await revealManifestationIdentity(ticketId));
    } catch (error) {
      showServiceError(error);
    }
  };

  const reportCurrentPresence = async () => {
    if (!thread) return;
    try {
      const isTyping = Date.now() - lastTypingRef.current < TYPING_WINDOW_MILLISECONDS;
      const response = await reportPresence(thread.id, isTyping ? 'TYPING' : 'VIEWING');
      setPresence(response);
      if (response.latestIncomingMessageId && !messages.some((message) => message.id === response.latestIncomingMessageId)) load();
    } catch {
      // a presença é só um aviso: falhou, a próxima consulta tenta de novo
    }
  };
  useEffect(() => {
    if (status === 'ready' && thread) reportCurrentPresence();
  }, [status === 'ready', thread?.id]);
  usePolling(() => {
    load();
    reportCurrentPresence();
  }, POLLING_INTERVAL_MILLISECONDS, status === 'ready');

  // atalhos da Caixa que agem no atendimento aberto (R, N, A, T, E)
  useEffect(() => {
    if (!commandRequest || status !== 'ready') return;
    const action = commandRequest.action;
    if (action === 'reply' && canReply) {
      setMode('reply');
      setFocusRequest((value) => value + 1);
    } else if (action === 'note') {
      setMode('note');
      setFocusRequest((value) => value + 1);
    } else if (action === 'take' && canTake) take();
    else if (action === 'transfer' && detail?.permissions.canTransfer) setDialog('transfer');
    else if (action === 'resolve' && detail?.permissions.canResolve) setDialog('resolve');
  }, [commandRequest?.nonce]);

  const refreshAll = async () => {
    await load();
    onChanged();
    onWhoIsReload?.();
  };

  const take = async () => {
    if (!ticket) return;
    try {
      await takeTicket(ticket.id);
      showSuccessSnackbar(t('serviceDesk.actions.taken'));
      refreshAll();
    } catch (error) {
      showServiceError(error);
    }
  };

  /** @param {Record<string, any>} patch @param {string} [successKey] */
  const patch = async (patch, successKey) => {
    if (!ticket) return;
    try {
      await updateTicket(ticket.id, patch);
      if (successKey) showSuccessSnackbar(t(successKey));
      refreshAll();
    } catch (error) {
      showServiceError(error);
    }
  };

  /** @param {{ text: string, attachments: any[], replyTemplateId: string | null }} input */
  const reply = async ({ text, attachments, replyTemplateId }) => {
    if (!thread) return false;
    try {
      await sendServiceMessage(thread.id, {
        text, fileIds: attachments.map((attachment) => attachment.fileId),
        ...(thread.channelType === 'EMAIL' ? { subject: buildReplySubject(thread.subject ?? ticket?.subject ?? '') } : {}),
        lastSeenMessageId: acknowledged.messageId, replyTemplateId,
      });
    } catch (error) {
      // mensagem nova da família desde a última vista: a linha recarrega e o envio espera a pessoa ver
      if (error instanceof ApiError && error.code === 'NEW_MESSAGES_SINCE_VIEW') {
        await load();
        setPresence((current) => ({ ...current, latestIncomingMessageId: error.details?.messageIds?.at?.(-1) ?? current.latestIncomingMessageId }));
      }
      throw error;
    }
    // a mensagem já saiu: as ações da resposta pronta que falharem não desfazem o envio
    if (replyTemplateId) await applyReplyTemplateActions(thread.id, replyTemplateId).catch(showServiceError);
    lastTypingRef.current = 0;
    const page = await listMessages(thread.id, { limit: 50 });
    setMessages(page.messages);
    setAcknowledged({ incomingId: latestIncomingMessageId(page.messages), messageId: page.messages.at(-1)?.id ?? null });
    refreshAll();
    return true;
  };

  /** @param {{ body: string, mentions: string[] }} input */
  const addNote = async (input) => {
    await addServiceNote(ticket ? { ticketId: ticket.id } : { threadId: thread?.id ?? null }, input);
    showSuccessSnackbar(t('serviceDesk.notes.added'));
    await load();
    return true;
  };

  const seeNewMessages = async () => {
    await load();
    const page = thread ? await listMessages(thread.id, { limit: 50 }) : { messages };
    setMessages(page.messages);
    setAcknowledged({ incomingId: latestIncomingMessageId(page.messages), messageId: page.messages.at(-1)?.id ?? null });
  };

  if (status === 'notFound') {
    return html`<div class="service-pane service-pane-empty" data-testid="service-ticket-unavailable">
      <${EmptyState} icon="IconInbox" title=${t('serviceDesk.pane.unavailableTitle')} subtitle=${t('serviceDesk.pane.unavailable')}
        action=${onBack ? html`<${Button} title=${t('serviceDesk.pane.backToList')} size="small" variant="secondary" onClick=${onBack} />` : null} />
    </div>`;
  }
  if (status === 'loading') return html`<div class="service-pane service-pane-empty"><${Loader} /></div>`;

  const team = teams.find((candidate) => candidate.id === ticket?.team?.id) ?? null;
  const isWindowClosed = thread?.channelType === 'WHATSAPP' && thread.isServiceWindowOpen === false;
  const latestIncoming = presence.latestIncomingMessageId ?? latestIncomingMessageId(messages);
  const collision = describeCollision({ others: presence.others, latestIncomingMessageId: latestIncoming, lastSeenIncomingMessageId: acknowledged.incomingId, viewerWorkspaceMemberId: viewerId });
  const nameOf = (/** @type {string} */ id) => {
    const member = (workspaceMembersState.value ?? []).find((candidate) => candidate.id === id);
    return member ? formatFullName(member.name) : null;
  };
  const contactName = thread ? getThreadDisplayName(thread) : ticket?.requester?.name ?? '';
  const statusTargets = ticket ? ['WAITING_FAMILY', 'OPEN', 'CLOSED'].filter((value) => canChangeTicketStatus(ticket.status, value)) : [];
  const templateValues = {
    'guardian.firstName': ticket?.requester?.name?.split(' ')[0] ?? null, 'guardian.name': ticket?.requester?.name ?? null,
    'student.firstName': ticket?.primaryStudent?.name?.split(' ')[0] ?? null, 'student.name': ticket?.primaryStudent?.name ?? null,
    'ticket.protocol': ticket?.protocol ?? null, 'ticket.subject': ticket?.subject ?? null, 'team.name': ticket?.team?.name ?? null,
    'agent.firstName': currentWorkspaceMemberState.value?.name?.firstName ?? null,
  };
  const isRestricted = ticket?.team?.teamKind === 'OMBUDSMAN' || ticket?.team?.teamKind === 'PROTECTION';
  const students = (detail?.students ?? []).length > 0 ? detail?.students ?? [] : ticket?.primaryStudent ? [{ personId: ticket.primaryStudent.id, name: ticket.primaryStudent.name }] : [];

  return html`<div class="service-pane" data-channel=${thread?.channelType ?? ticket?.channel} data-testid="service-ticket-pane">
    <header class="service-pane-header">
      <div class="service-pane-title-row">
        ${onBack ? html`<${LightIconButton} icon="IconChevronLeft" size="medium" ariaLabel=${t('serviceDesk.pane.backToList')} onClick=${onBack} />` : null}
        ${ticket ? html`<span class="service-protocol" data-size="large">${ticket.protocol}</span>` : null}
        ${ticket && isEditingSubject
          ? html`<input class="text-input service-subject-input" value=${subjectDraft} maxLength=${200} aria-label=${t('serviceDesk.pane.subject')} autoFocus
              onInput=${(/** @type {Event} */ event) => setSubjectDraft(/** @type {HTMLInputElement} */ (event.currentTarget).value)}
              onBlur=${() => setIsEditingSubject(false)}
              onKeyDown=${(/** @type {KeyboardEvent} */ event) => {
                if (event.key === 'Escape') setIsEditingSubject(false);
                if (event.key === 'Enter' && subjectDraft.trim()) {
                  setIsEditingSubject(false);
                  patch({ subject: subjectDraft.trim() }, 'serviceDesk.pane.subjectSaved');
                }
              }} />`
          : html`<button type="button" class="service-subject" title=${ticket ? t('serviceDesk.pane.editSubject') : undefined} disabled=${!ticket || !isOpen}
              onClick=${() => {
                setSubjectDraft(ticket?.subject ?? '');
                setIsEditingSubject(true);
              }}>${ticket?.subject ?? t('serviceDesk.pane.threadWithoutTicket', { name: contactName })}</button>`}
        <${RestrictedBadge} ticket=${ticket} />
        <span class="service-pane-title-spacer"></span>
        ${showWhoIsToggle
          ? html`<${LightIconButton} icon=${isWhoIsOpen ? 'IconLayoutSidebarRightCollapse' : 'IconLayoutSidebarRightExpand'} size="medium"
              ariaLabel=${isWhoIsOpen ? t('serviceDesk.whoIs.close') : t('serviceDesk.whoIs.open')} onClick=${onToggleWhoIs} data-testid="service-who-toggle" />`
          : null}
      </div>
      ${ticket
        ? html`<div class="service-pane-meta">
            ${ticket.ticketType !== 'REQUEST' ? html`<${TicketValueTag} field="ticketType" value=${ticket.ticketType} />` : null}
            <${MenuButton} label=${html`<${TicketValueTag} field="status" value=${ticket.status} />`} className="service-tag-button" testId="service-status-menu"
              ariaLabel=${t('serviceDesk.pane.changeStatus')} disabled=${statusTargets.length === 0 && !isOpen}>
              ${(/** @type {() => void} */ close) => html`
                ${statusTargets.map((value) => html`<${MenuItem} key=${value} text=${ticketValueLabel('status', value)} onClick=${() => {
                  close();
                  if (value === 'OPEN' && ticket.status === 'RESOLVED') reopenTicket(ticket.id).then(refreshAll).catch(showServiceError);
                  else patch({ status: value }, 'serviceDesk.pane.statusSaved');
                }} />`)}
                ${isOpen ? html`<${MenuItem} text=${t('serviceDesk.actions.resolve')} icon="IconCircleCheck" onClick=${() => {
                  close();
                  setDialog('resolve');
                }} />` : null}
              `}
            <//>
            <${MenuButton} label=${html`<${TicketValueTag} field="priority" value=${ticket.priority} icon="IconFlag" />`} className="service-tag-button"
              ariaLabel=${t('serviceDesk.pane.changePriority')} disabled=${!isOpen}>
              ${(/** @type {() => void} */ close) => TICKET_PRIORITIES.map((value) => html`<${MenuItem} key=${value} text=${ticketValueLabel('priority', value)} selected=${ticket.priority === value}
                onClick=${() => {
                  close();
                  patch({ priority: value });
                }} />`)}
            <//>
            <span class="service-pane-team"><${Icon} name="IconUsersGroup" size=${14} />${ticket.team?.name ?? ''}</span>
            <${MenuButton} label=${ticket.assignee?.name ?? t('serviceDesk.pane.noAssignee')} icon="IconUserCircle" ariaLabel=${t('serviceDesk.pane.changeAssignee')}
              disabled=${!isOpen || isRestricted && !myTeamIds.has(ticket.team?.id ?? '')} testId="service-assignee-menu">
              ${(/** @type {() => void} */ close) => (team?.members ?? []).map((member) => html`<${MenuItem} key=${member.workspaceMemberId} text=${member.name}
                contextualText=${t(`serviceDesk.agentStatus.${member.status}`)} selected=${ticket.assignee?.workspaceMemberId === member.workspaceMemberId}
                onClick=${() => {
                  close();
                  patch({ assigneeWorkspaceMemberId: member.workspaceMemberId }, 'serviceDesk.pane.assigneeSaved');
                }} />`)}
            <//>
            <${SlaChip} ticket=${ticket} />
            <${ServiceChannelIcon} channel=${ticket.channel} />
            <${MenuButton} label=${ticket.tags.length ? ticket.tags.map((tag) => ticketValueLabel('tags', tag)).join(', ') : t('serviceDesk.pane.tags')} icon="IconTag"
              ariaLabel=${t('serviceDesk.pane.tags')} disabled=${!isOpen}>
              ${() => SERVICE_TAGS.map((value) => html`<${MenuItem} key=${value} text=${ticketValueLabel('tags', value)} multiSelect=${true} selected=${ticket.tags.includes(value)}
                onClick=${() => patch({ tags: ticket.tags.includes(value) ? ticket.tags.filter((tag) => tag !== value) : [...ticket.tags, value] })} />`)}
            <//>
            ${ticket.category ? html`<span class="service-pane-category"><${Icon} name="IconTag" size=${12} />${ticketValueLabel('category', ticket.category)}</span>` : null}
          </div>
          <div class="service-pane-actions">
            ${canTake ? html`<${Button} title=${t('serviceDesk.actions.take')} icon="IconUserCheck" size="small" accent="blue" hotkeyLabel="A" onClick=${take} data-testid="service-take" />` : null}
            ${detail?.permissions.canTransfer ? html`<${Button} title=${t('serviceDesk.actions.transfer')} icon="IconTransferIn" size="small" variant="secondary" hotkeyLabel="T" onClick=${() => setDialog('transfer')} data-testid="service-transfer" />` : null}
            ${isOpen && !isRestricted ? html`<${Button} title=${t('serviceDesk.actions.consult')} icon="IconMessageQuestion" size="small" variant="secondary" onClick=${() => setDialog('consult')} data-testid="service-consult" />` : null}
            ${detail?.permissions.canResolve ? html`<${Button} title=${t('serviceDesk.actions.resolve')} icon="IconCircleCheck" size="small" variant="secondary" hotkeyLabel="E" onClick=${() => setDialog('resolve')} data-testid="service-resolve" />` : null}
            ${ticket.status === 'RESOLVED' ? html`<${Button} title=${t('serviceDesk.actions.reopen')} icon="IconRefresh" size="small" variant="secondary" onClick=${() => reopenTicket(ticket.id).then(refreshAll).catch(showServiceError)} />` : null}
            <${MenuButton} label=${t('serviceDesk.actions.more')} icon="IconDots" testId="service-more-menu" ariaLabel=${t('serviceDesk.actions.more')}>
              ${(/** @type {() => void} */ close) => html`
                ${thread ? html`<${MenuItem} text=${t('serviceDesk.actions.changePurpose')} icon="IconSwitchHorizontal" onClick=${() => {
                  close();
                  setDialog('purpose');
                }} />` : null}
                ${thread && isOpen ? html`<${MenuItem} text=${t('serviceDesk.actions.split')} icon="IconLayoutRows" onClick=${() => {
                  close();
                  setSplitSelection(new Set());
                }} />` : null}
                ${detail?.permissions.canMerge ? html`<${MenuItem} text=${t('serviceDesk.actions.merge')} icon="IconArrowMerge" onClick=${() => {
                  close();
                  setDialog('merge');
                }} />` : null}
                ${detail?.permissions.canEscalate ? html`<${DropdownMenuSeparator} />
                  <${MenuItem} text=${t('serviceDesk.actions.escalateOmbudsman')} icon="IconSpeakerphone" onClick=${() => {
                    close();
                    setDialog('ombudsman');
                  }} />
                  <${MenuItem} text=${t('serviceDesk.actions.escalateProtection')} icon="IconShieldLock" accent="danger" onClick=${() => {
                    close();
                    setDialog('protection');
                  }} />` : null}
                <${DropdownMenuSeparator} />
                ${!thread && ticket.requester && isOpen ? html`<${MenuItem} text=${t('serviceDesk.actions.replyByWhatsapp')} icon="IconBrandWhatsapp" onClick=${() => {
                  close();
                  openNewConversationPanel({ defaultChannelType: 'WHATSAPP', personId: ticket.requester?.id ?? null, onCreated: () => refreshAll() });
                }} />` : null}
                <${MenuItem} text=${t('serviceDesk.actions.openRecord')} icon="IconExternalLink" onClick=${() => {
                  close();
                  openRecord({ objectNameSingular: 'serviceTicket', recordId: ticket.id });
                }} />
              `}
            <//>
          </div>`
        : html`<div class="service-pane-actions">
            ${thread?.purpose !== 'SERVICE' || !thread?.activeTicket
              ? html`<${Button} title=${t('serviceDesk.actions.changePurpose')} icon="IconSwitchHorizontal" size="small" variant="secondary" onClick=${() => setDialog('purpose')} />`
              : null}
            <${Button} title=${t('serviceDesk.actions.escalateProtection')} icon="IconShieldLock" size="small" variant="secondary" accent="danger" onClick=${() => setDialog('protection')} />
          </div>`}
    </header>
    ${thread ? html`<${BotConversationBanner} thread=${thread} onChanged=${refreshAll} />` : null}
    ${splitSelection
      ? html`<div class="service-split-bar" role="status" data-testid="service-split-bar">
          <${Icon} name="IconLayoutRows" size=${14} /><span>${t('serviceDesk.split.selecting', { count: splitSelection.size })}</span>
          <${Button} title=${t('common.cancel')} size="small" variant="secondary" onClick=${() => setSplitSelection(null)} />
          <${Button} title=${t('serviceDesk.split.next')} size="small" accent="blue" disabled=${splitSelection.size === 0} onClick=${() => setDialog('split')} />
        </div>`
      : null}
    <div class="service-pane-scroll">
      ${ticket?.team?.teamKind === 'PROTECTION' ? html`<${ProtectionPanel} ticketId=${ticket.id} protection=${detail?.protection ?? null} onChanged=${refreshAll} />` : null}
      ${ticket?.ticketType === 'OMBUDSMAN' ? html`<${ManifestationPanel} ticket=${ticket} manifestation=${detail?.manifestation && revealedIdentity
        ? { ...detail.manifestation, contact: { name: revealedIdentity.name, email: revealedIdentity.email, phone: revealedIdentity.phone } } : detail?.manifestation ?? null}
        onRevealIdentity=${revealIdentity} onChanged=${refreshAll} />` : null}
      ${ticket?.ticketType === 'RETENTION' ? html`<${RetentionPanel} ticket=${ticket} hasTransferDocumentTicket=${familyTickets.some((candidate) => candidate.category === 'TRANSFER' && isTicketOpen(candidate))} />` : null}
      ${ticket ? html`<${ConsultationsPanel} consultations=${detail?.consultations ?? []} viewerWorkspaceMemberId=${viewerId} isOwner=${ticket.assignee?.workspaceMemberId === viewerId} onChanged=${refreshAll} />` : null}
      ${ticket ? html`<${CsatPanel} ticket=${ticket} csat=${detail?.csat ?? null} />` : null}
      ${ticket?.summary && !thread ? html`<section class="service-ticket-panel"><h3 class="service-ticket-panel-title"><${Icon} name="IconNotes" size=${14} />${t('serviceDesk.pane.summary')}</h3><p class="service-summary">${ticket.summary}</p></section>` : null}
      <${ServiceTimeline}
        messages=${messages}
        notes=${detail?.notes ?? []}
        events=${detail?.events ?? []}
        isEmail=${thread?.channelType === 'EMAIL'}
        contactName=${contactName}
        viewerWorkspaceMemberId=${viewerId}
        nameOf=${nameOf}
        selection=${splitSelection}
        onToggleSelection=${(/** @type {string} */ messageId) => setSplitSelection((current) => {
          const next = new Set(current ?? []);
          if (next.has(messageId)) next.delete(messageId);
          else next.add(messageId);
          return next;
        })}
        emptyText=${thread ? undefined : t('serviceDesk.timeline.manualTicket')}
        onEditNote=${async (/** @type {any} */ note, /** @type {string} */ body) => {
          try {
            await editServiceNote(note.id, body);
            await load();
            return true;
          } catch (error) {
            showServiceError(error);
            return false;
          }
        }}
        onDeleteNote=${async (/** @type {any} */ note) => {
          try {
            await deleteServiceNote(note.id);
            await load();
          } catch (error) {
            showServiceError(error);
          }
        }}
      />
    </div>
    ${ticket?.ticketType === 'CONSULTATION' && !thread
      ? null
      : html`<${ServiceComposer}
          key=${`${ticketId ?? ''}-${threadId ?? ''}`}
          mode=${canReply ? mode : 'note'}
          onModeChange=${setMode}
          canReply=${canReply}
          replyDisabledReason=${!thread ? t('serviceDesk.composer.noThread') : t('serviceDesk.composer.ticketClosed')}
          isWindowClosed=${isWindowClosed}
          onSendTemplate=${thread ? () => setDialog('templateMessage') : undefined}
          ticketId=${ticket?.id ?? null}
          templateValues=${templateValues}
          collision=${collision}
          onSeeNewMessages=${seeNewMessages}
          onReply=${reply}
          onNote=${addNote}
          onTyping=${() => {
            const wasIdle = Date.now() - lastTypingRef.current > TYPING_WINDOW_MILLISECONDS;
            lastTypingRef.current = Date.now();
            if (wasIdle && thread) reportPresence(thread.id, 'TYPING').then(setPresence).catch(() => {});
          }}
          focusRequest=${focusRequest}
        />`}
    ${dialog === 'transfer' && ticket ? html`<${TransferDialog} ticket=${ticket} teams=${teams} notifyFamilyDefault=${notifyFamilyDefault} onClose=${() => setDialog(null)}
      onDone=${() => {
        setDialog(null);
        refreshAll();
      }} />` : null}
    ${dialog === 'consult' && ticket ? html`<${ConsultDialog} ticket=${ticket} teams=${teams} students=${students} onClose=${() => setDialog(null)}
      onDone=${() => {
        setDialog(null);
        refreshAll();
      }} />` : null}
    ${dialog === 'resolve' && ticket ? html`<${ResolveDialog} ticket=${ticket} onClose=${() => setDialog(null)} onDone=${() => {
      setDialog(null);
      refreshAll();
    }} />` : null}
    ${dialog === 'split' && ticket && splitSelection ? html`<${SplitDialog} ticket=${ticket} teams=${teams} messageIds=${[...splitSelection]} onClose=${() => setDialog(null)}
      onDone=${(/** @type {any} */ created) => {
        setDialog(null);
        setSplitSelection(null);
        refreshAll();
        if (created?.id) navigate(`/service/inbox/${created.id}`);
      }} />` : null}
    ${dialog === 'merge' && ticket ? html`<${MergeDialog} ticket=${ticket} candidates=${familyTickets} onClose=${() => setDialog(null)} onDone=${() => {
      setDialog(null);
      refreshAll();
    }} />` : null}
    ${dialog === 'protection' ? html`<${ProtectionDialog} target=${ticket ? { ticketId: ticket.id } : { threadId: thread?.id ?? null }} onClose=${() => setDialog(null)}
      onDone=${() => {
        setDialog(null);
        onChanged();
        navigate('/service/inbox');
      }} />` : null}
    ${dialog === 'ombudsman' && ticket ? html`<${OmbudsmanEscalateDialog} ticket=${ticket} onClose=${() => setDialog(null)} onDone=${() => {
      setDialog(null);
      onChanged();
      navigate('/service/inbox');
    }} />` : null}
    ${dialog === 'purpose' && thread ? html`<${PurposeDialog} threadId=${thread.id} currentPurpose=${thread.purpose ?? null} teams=${teams} onClose=${() => setDialog(null)}
      onDone=${() => {
        setDialog(null);
        refreshAll();
      }} />` : null}
    ${dialog === 'templateMessage' && thread ? html`<${TemplateMessageModal} thread=${thread} onClose=${() => setDialog(null)} onSent=${() => {
      setDialog(null);
      load();
    }} />` : null}
  </div>`;
}

